import {
  IndicatorBadge,
  InlineFormattedNumber,
  SideEnum,
  isOption,
  toBigWithDefault,
  useCurrency,
  useSecuritiesContext,
  type CardProps,
  type Order,
  type OrderAnalytic,
  type Security,
} from '@talos/kyoko';
import Big from 'big.js';
import { useMemo } from 'react';
import { MakerTakerChart } from './MakerTakerChart';
import { DataCell, GridContainer, HeaderCell } from './styles';

export function TradingCardContent({
  latestAnalytics,
  order,
  leg: propsLeg,
}: {
  order: Order;
  latestAnalytics: Record<number, OrderAnalytic>;
  leg?: number;
} & CardProps) {
  const { securitiesBySymbol } = useSecuritiesContext();
  const isMultileg = order.LegSummary && propsLeg !== undefined;
  // Analytics for non-multileg are accessed by indexing leg 1.
  // And legs are displayed 1 and 2, but indexed 0 and 1
  const leg = isMultileg ? propsLeg - 1 : 1;

  const orderAnalytics = latestAnalytics[propsLeg ?? 1];
  const { maker, taker } = useMemo(() => {
    let maker = 0;
    let taker = 0;
    if (orderAnalytics) {
      const { CumQty, CrossingFilledQty } = orderAnalytics;
      if (CumQty != null && !Big(CumQty).eq(0) && CrossingFilledQty != null) {
        maker = Big(CumQty).minus(CrossingFilledQty).div(CumQty).toNumber();
        taker = Big(CrossingFilledQty).div(CumQty).toNumber();
      }
    }
    return {
      maker,
      taker,
    };
  }, [orderAnalytics]);

  const currentLegSummary = useMemo(() => {
    return order.LegSummary?.find(legSummary => legSummary.LegIndex === leg);
  }, [leg, order.LegSummary]);

  const security = securitiesBySymbol.get(order.Symbol);
  const bps: string = useMemo(() => {
    if (!currentLegSummary) {
      return getBps(order, security);
    } else {
      return getBps({ ...currentLegSummary, OrderQty: currentLegSummary.CumQty as string }, security);
    }
  }, [currentLegSummary, order, security]);

  const cumFee = useMemo(() => {
    if (isMultileg) {
      return currentLegSummary?.CumFee;
    }
    return order.CumFee;
  }, [currentLegSummary?.CumFee, isMultileg, order.CumFee]);
  const feeCurrency = useCurrency(isMultileg ? currentLegSummary?.FeeCurrency : order.FeeCurrency);

  if (!orderAnalytics) {
    return null;
  }

  const isCumFeeZero = Big(cumFee ?? 0).eq(0);
  return (
    <div>
      <GridContainer isMultileg={isMultileg}>
        {isMultileg ? <HeaderCell>Leg</HeaderCell> : null}
        <HeaderCell>Fees</HeaderCell>
        <HeaderCell align="right">Amount</HeaderCell>
        <HeaderCell align="right">BPS</HeaderCell>
        {isMultileg ? (
          <DataCell>
            <IndicatorBadge children={leg + 1} />
          </DataCell>
        ) : null}
        <DataCell>Exchange</DataCell>
        <DataCell align="right">
          <InlineFormattedNumber
            round
            number={cumFee}
            increment={feeCurrency?.DefaultIncrement}
            currency={feeCurrency?.Symbol}
          />
        </DataCell>
        <DataCell align="right">
          {isCumFeeZero ? 'N/A' : <InlineFormattedNumber number={bps} increment="0.01" />}
        </DataCell>
      </GridContainer>
      <MakerTakerChart maker={maker} taker={taker} />
    </div>
  );
}

// https://talostrading.atlassian.net/browse/UI-2839
function getBps(
  { Side, AvgPx, AvgPxAllIn, OrderQty, CumFee }: Pick<Order, 'Side' | 'AvgPx' | 'AvgPxAllIn' | 'OrderQty' | 'CumFee'>,
  security: Security | undefined
): string {
  if (
    AvgPx === undefined ||
    toBigWithDefault(AvgPx, 0).eq(0) ||
    AvgPxAllIn === undefined ||
    toBigWithDefault(AvgPxAllIn, 0).eq(0)
  ) {
    return '';
  }

  // https://talostrading.atlassian.net/browse/UI-3891
  if (isOption(security) && security?.BaseCurrency === security?.QuoteCurrency) {
    return toBigWithDefault(CumFee, 0)
      .div(OrderQty || 1)
      .mul(10_000)
      .toFixed(2);
  }

  if (Side === SideEnum.Sell) {
    return Big(Big(Big(AvgPx).minus(AvgPxAllIn)).div(AvgPx || 1))
      .mul(10_000)
      .toFixed(2);
  } else if (Side === SideEnum.Buy) {
    return Big(Big(Big(AvgPxAllIn).minus(AvgPx)).div(AvgPx || 1))
      .mul(10_000)
      .toFixed(2);
  }

  return '';
}
